import React, { useEffect } from 'react';
import {
    Container, Typography, Box, Paper, Divider, TextField, Button, Stack, 
    Grid, Switch, FormControlLabel, Tooltip
} from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useAppStore } from '../../../../../store/appStore.tsx';
import { useValue } from '../../../../../context/ContextProvider';
import { updateConfigAlarma } from '../../../../../actions/alarmaActions';

// --- Generador del estado inicial para la configuración GPRS ---
export const generarEstadoInicialGprs = () => ({
    hab: 1,
    apn: "internet.tigo.bo",
});

function ConfiguracionGprsForm({config, setConfig, state, setState, originalConfigRef}) {
    const { jsonOutput, formDirty, jsonNeedsUpdate } = state;
    const theme = useTheme();
    const { currentUser, device, startLoading, endLoading } = useAppStore();
    const { dispatch } = useValue();

    useEffect(() => {
        if (!jsonOutput) {
            setState(prev => ({
                ...prev,
                jsonOutput: JSON.stringify(config, null, 2),
                jsonNeedsUpdate: false
            }));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const originalConfigString = JSON.stringify(originalConfigRef.current);
        const currentConfigString = JSON.stringify(config);
        const isDirty = currentConfigString !== originalConfigString;
        
        let needsUpdate = true;
        try {
            const jsonObject = JSON.parse(jsonOutput || '{}');
            needsUpdate = JSON.stringify(jsonObject) !== currentConfigString;
        } catch (e) { needsUpdate = true; }

        if (isDirty !== formDirty || needsUpdate !== jsonNeedsUpdate) {
            setState(prev => ({ ...prev, formDirty: isDirty, jsonNeedsUpdate: needsUpdate }));
        }

    }, [config, jsonOutput, formDirty, jsonNeedsUpdate, originalConfigRef, setState]);

    const handleChange = (event) => {
        const { name, value, type, checked } = event.target;
        const valorFinal = type === 'checkbox' ? (checked ? 1 : 0) : value;
        setConfig(prev => ({ ...prev, [name]: valorFinal }));
    };

    const handleGenerateJson = () => {
        setState(prev => ({
            ...prev,
            jsonOutput: JSON.stringify(config, null, 2),
            jsonNeedsUpdate: false
        }));
    };

    const sendConfig = async() => {
        /*//console.log("--- Enviando Configuración GPRS JSON ---", config);
        alert("Configuración GPRS enviada. Revisa la consola.");
        originalConfigRef.current = JSON.parse(JSON.stringify(config));
        setState(prev => ({ ...prev, formDirty: false }));
        */
        //console.log("--- Enviando Configuración JSON ---", config);
        startLoading();
        setState(prev => ({ ...prev, configId: "---", estado: "Esperando respuesta del servidor/dispositivo", color: "black"}));
        const updateResult = await updateConfigAlarma(currentUser, device.devId, "CMD_SET_CONFIG_GPRS", config, dispatch);
        //console.log("updateResult.insertId: ", updateResult?.insertId);

        if (updateResult?.deviceResponse === 0) {
            // Si el envío es exitoso, actualizamos la configuración original
            originalConfigRef.current = JSON.parse(JSON.stringify(config));
            setState(prev => ({ ...prev, formDirty: false, configId: updateResult.insertId, estado: "Última configuración actualizada con éxito.", color: "green"}));
            alert(`Configuración enviada con éxito (ID: ${updateResult.insertId})`);
        } else {
            setState(prev => ({ ...prev, configId: 0, estado: "Error. Última configuración NO actualizada.", color: "red"}));
            alert("Error al enviar la configuración.");
        }
        endLoading();
    };

    const handleCancel = () => {
        if (window.confirm("¿Seguro que quieres descartar los cambios?")) {
            setConfig(originalConfigRef.current);
        }
    };

    return (
        <Container maxWidth="lg" sx={{ mt: 4 }}>
            <Paper elevation={3} sx={{ p: 3 }}>
                <Typography variant="h5" component="h1" gutterBottom>Configuración GPRS</Typography>
                <Divider sx={{ mb: 2 }} />
                <Box component="form" onSubmit={(e) => { e.preventDefault(); sendConfig(); }}>
                    <Grid container spacing={4}>
                        {/* Columna izquierda: Formulario */}
                        <Grid item xs={12} md={6}>
                            <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                                <Grid container spacing={3}>
                                    <Grid item xs={12}>
                                        <FormControlLabel control={<Switch name="hab" checked={!!config.hab} onChange={handleChange} />} label="Habilitar GPRS" />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField fullWidth name="apn" label="APN" value={config.apn} onChange={handleChange} inputProps={{ minLength: 2, maxLength: 46 }} 
                                            InputProps={{endAdornment: (
                                                <Tooltip title="Access Point Name"><InfoOutlined sx={{ color: 'action.active' }} /></Tooltip>
                                            )}}
                                        />
                                    </Grid>
                                </Grid>
                            </Paper>
                        </Grid>

                        {/* Columna derecha: JSON y Resumen */}
                        <Grid item xs={12} md={6} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Paper variant="outlined" sx={{ p: 2, flexGrow: 1, display: 'flex', flexDirection: 'column', position: 'relative', pt: 4 }}>
                                <Typography variant="caption" component="div" sx={{ position: 'absolute', top: 0, left: 12, transform: 'translateY(-50%)', bgcolor: 'background.paper', px: 1, color: 'primary.main', fontWeight: 'bold' }}>JSON DE CONFIGURACIÓN</Typography>
                                
                                {/* --- CORRECCIÓN AQUÍ: Usamos un Box para el scroll y Typography para el texto --- */}
                                <Box sx={{
                                    flexGrow: 1,
                                    overflowY: 'auto',
                                    border: `1px solid ${jsonNeedsUpdate ? theme.palette.warning.main : theme.palette.divider}`,
                                    borderRadius: 1,
                                    p: 1.5,
                                    backgroundColor: jsonNeedsUpdate ? '#ffe0b2' : 'background.paper',
                                    minHeight: '200px',
                                }}>
                                    <Typography component="pre" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap', m: 0, fontSize: '0.875rem' }}>
                                        {jsonOutput}
                                    </Typography>
                                </Box>

                                <Button fullWidth variant="contained" color={jsonNeedsUpdate ? "warning" : "primary"} onClick={handleGenerateJson} sx={{ mt: 1 }}>
                                    {jsonNeedsUpdate ? "Actualizar JSON" : "Generar JSON"}
                                </Button>
                            </Paper>
                            <Paper variant="outlined" sx={{ p: 2, position: 'relative', pt: 4 }}>
                                <Typography variant="caption" component="div" sx={{ position: 'absolute', top: 0, left: 12, transform: 'translateY(-50%)', bgcolor: 'background.paper', px: 1, color: 'primary.main', fontWeight: 'bold' }}>RESUMEN DE CONFIGURACIÓN</Typography>
                                <Typography variant="body2">**Habilitado:** {config.hab ? 'Sí' : 'No'}</Typography>
                                <Typography variant="body2">**APN:** {config.apn}</Typography>
                                <Box sx={{ mt: 2 }}>
                                    <Typography variant="body2" color={formDirty ? 'error' : 'inherit'}>**Formulario Modificado:** {formDirty ? 'Sí' : 'No'}</Typography>
                                    <Typography variant="body2" color={jsonNeedsUpdate ? 'warning.main' : 'inherit'}>**JSON Actualizado:** {jsonNeedsUpdate ? 'No' : 'Sí'}</Typography>
                                </Box>
                            </Paper>
                        </Grid>
                        
                        {/* Fila inferior: Resultados del envio de la configuracion al dispositivo */}
                        <Grid item xs={12} md={12}>
                            <Paper variant="outlined" sx={{ p: 2, height: '100%', position: 'relative', pt: 4 }}>
                                <Typography variant="caption" component="div" sx={{position: 'absolute', top: 0, left: 12, transform: 'translateY(-50%)', bgcolor: 'background.paper', px: 1, color: 'primary.main', fontWeight: 'bold', fontSize: '0.8rem', textTransform: 'uppercase'}}>
                                    Resultado envío al Dispositivo
                                </Typography>
                                <Box>
                                    <Typography variant="body2" component="p" sx={{ ml: 2, fontFamily: 'monospace', mb: 0.5 }}>
                                        <strong>ConfigId: </strong>{state.configId}
                                    </Typography>
                                    <Typography variant="body2" component="p" sx={{ ml: 2, fontFamily: 'monospace', mb: 0.5, color: state.color }}>
                                        <strong>Estado: </strong>{state.estado}
                                    </Typography>
                                </Box>
                            </Paper>
                        </Grid>

                        {/* Botones de Acción */}
                        <Grid item xs={12}>
                            <Stack direction="row" spacing={2} justifyContent="center" sx={{ mt: 2 }}>
                                <Button variant="outlined" color="secondary" onClick={handleCancel} disabled={!formDirty}>Cancelar</Button>
                                <Button type="submit" variant="contained" disabled={jsonNeedsUpdate}>Enviar</Button>
                            </Stack>
                        </Grid>
                    </Grid>
                </Box>
            </Paper>
        </Container>
    );
}

export default ConfiguracionGprsForm;